import React, { useEffect } from "react";
import OverviewData from "./OverviewData";
import AdminSideBar from "../../common/AdminSideBar/AdminSideBar";
import { useSelector, useDispatch } from "react-redux";
import { getUsers } from "../../redux/userSlice";
import { getTherapists, getTherapistAppointmentStats } from "../../redux/therapistSlice";
import { getAllTherapistPayments } from "../../redux/therapistPeymentSlice";
import { getAllRequests } from "../../redux/requestSlice";
import { FiTrendingUp, FiUsers, FiUserCheck, FiActivity } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

const AdminHome = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { users } = useSelector((state) => state.users);
  const { therapists, appointmentStatsMap = {} } = useSelector((state) => state.therapist);
  const { therapistPayments = [] } = useSelector((state) => state.therapistPayments);
  const { requests: allAdminRequests = [] } = useSelector((state) => state.requests);

  useEffect(() => {
    dispatch(getUsers());
    dispatch(getTherapists());
    dispatch(getAllTherapistPayments());
    dispatch(getAllRequests());
  }, [dispatch]);

  useEffect(() => {
    if (!Array.isArray(therapists) || therapists.length === 0) return;

    therapists.forEach((therapist) => {
      if (therapist?._id) {
        dispatch(getTherapistAppointmentStats(therapist._id));
      }
    });
  }, [dispatch, therapists]);

  const parseDate = (value) => {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const parseAmount = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  const isRefundedRequest = (request) => {
    return (
      request?.payment?.status === "refunded" ||
      request?.payment?.details?.refund?.status === "succeeded"
    );
  };

  const getRequestDate = (request) => {
    const selectedSlot =
      request?.selectedSlot && typeof request.selectedSlot === "object"
        ? request.selectedSlot
        : null;

    const rawDate =
      selectedSlot?.startDate ??
      selectedSlot?.startTime ??
      selectedSlot?.start ??
      request?.sessionDate ??
      request?.startDate ??
      request?.startTime ??
      request?.appointmentDate ??
      request?.date ??
      request?.createdAt;

    return parseDate(rawDate);
  };

  const getRequestPrice = (request) => {
    const selectedSlot =
      request?.selectedSlot && typeof request.selectedSlot === "object"
        ? request.selectedSlot
        : null;

    return (
      parseAmount(selectedSlot?.price) ||
      parseAmount(request?.price) ||
      parseAmount(request?.payment?.amount) ||
      parseAmount(request?.payment?.details?.amount) ||
      0
    );
  };

  const formatTrend = (current, previous) => {
    const previousSafe = Number(previous) || 0;
    const currentSafe = Number(current) || 0;

    if (previousSafe === 0) {
      if (currentSafe === 0) return { text: "0%", color: "text-gray-500" };
      return { text: "+100%", color: "text-green-600" };
    }

    const pct = ((currentSafe - previousSafe) / previousSafe) * 100;
    const rounded = Math.round(pct * 10) / 10;
    const prefix = rounded > 0 ? "+" : "";
    const color = rounded >= 0 ? "text-green-600" : "text-red-600";
    return { text: `${prefix}${rounded}%`, color };
  };

  const computeDashboardMetrics = () => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    const prevMonthDate = new Date(currentYear, currentMonth - 1, 1);
    const prevMonth = prevMonthDate.getMonth();
    const prevYear = prevMonthDate.getFullYear();

    const allRequests = therapists.flatMap((therapist) =>
      Array.isArray(therapist?.requests) ? therapist.requests : [],
    );

    const currentMonthRequests = allRequests.filter((request) => {
      const date = getRequestDate(request);
      if (!date) return false;
      return (
        date.getMonth() === currentMonth &&
        date.getFullYear() === currentYear &&
        !isRefundedRequest(request)
      );
    });

    const prevMonthRequests = allRequests.filter((request) => {
      const date = getRequestDate(request);
      if (!date) return false;
      return (
        date.getMonth() === prevMonth &&
        date.getFullYear() === prevYear &&
        !isRefundedRequest(request)
      );
    });

    const sessionsFromStats = therapists.reduce((sum, therapist) => {
      const therapistId = therapist?._id;
      if (!therapistId) return sum;
      return sum + parseAmount(appointmentStatsMap?.[therapistId]?.totalAppointments);
    }, 0);

    const sessionsFromTherapists = therapists.reduce(
      (sum, therapist) =>
        sum + parseAmount(therapist?.therapyPerformed || therapist?.totalSessions),
      0,
    );

    // Prefer backend appointment stats; fallback to therapist fields; then request count.
    const sessionsCurrent =
      sessionsFromStats || sessionsFromTherapists || allRequests.length || 0;

    const sessionsPrev = prevMonthRequests.length;

    const incomeCurrentFromRequests = currentMonthRequests.reduce(
      (sum, request) => sum + getRequestPrice(request),
      0,
    );

    const incomePrevFromRequests = prevMonthRequests.reduce(
      (sum, request) => sum + getRequestPrice(request),
      0,
    );

    // therapistId → hourlyPrice map'i
    const hourlyPriceMap = {};
    therapistPayments.forEach((tp) => {
      const tid = typeof tp.therapistId === "object" ? tp.therapistId?._id : tp.therapistId;
      if (tid && tp.hourlyPrice) hourlyPriceMap[tid] = parseAmount(tp.hourlyPrice);
    });
    // therapists listesinden de hourlyRate/hourlyPrice çek (fallback)
    therapists.forEach((t) => {
      if (!hourlyPriceMap[t._id]) {
        const rate = parseAmount(t.therapistDetail?.hourlyRate || t.therapistPayment?.hourlyPrice || t.hourlyPrice);
        if (rate) hourlyPriceMap[t._id] = rate;
      }
    });


    const unpaidStatuses = ["pending", "cancelled", "canceled", "rejected"];

    const startOfMonth = new Date(currentYear, currentMonth, 1);
    const startOfPrevMonth = new Date(prevYear, prevMonth, 1);
    const startOfThisMonth = new Date(currentYear, currentMonth + 1, 1);

    const calcIncome = (reqs, fromDate, toDate) =>
      reqs.filter((req) => {
        if (unpaidStatuses.includes(req.status)) return false;
        if (isRefundedRequest(req)) return false;
        const date = getRequestDate(req) || parseDate(req.meetingId?.startedAtDate) || parseDate(req.createdAt);
        if (!date) return false;
        return date >= fromDate && date < toDate;
      }).reduce((sum, req) => {
        const tid = typeof req.therapistId === "object" ? req.therapistId?._id : req.therapistId;
        const price = parseAmount(req.price) || parseAmount(req.payment?.amount) || hourlyPriceMap[tid] || 0;
        return sum + price;
      }, 0);

    const calcTotalIncome = (reqs) =>
      reqs.filter((req) => {
        if (unpaidStatuses.includes(req.status)) return false;
        if (isRefundedRequest(req)) return false;
        return true;
      }).reduce((sum, req) => {
        const tid = typeof req.therapistId === "object" ? req.therapistId?._id : req.therapistId;
        const price = parseAmount(req.price) || parseAmount(req.payment?.amount) || hourlyPriceMap[tid] || 0;
        return sum + price;
      }, 0);

    const incomeFromAdminRequests     = calcIncome(allAdminRequests, startOfMonth, startOfThisMonth);
    const incomeFromAdminRequestsPrev = calcIncome(allAdminRequests, startOfPrevMonth, startOfMonth);

    const incomeCurrent = incomeFromAdminRequests || incomeCurrentFromRequests || 0;
    const incomePrev    = incomeFromAdminRequestsPrev || incomePrevFromRequests;
    const incomeTotal   = calcTotalIncome(allAdminRequests);

    return {
      totalSessions: sessionsCurrent,
      totalSessionsTrend: formatTrend(sessionsCurrent, sessionsPrev),
      monthlyIncome: incomeCurrent,
      monthlyIncomeTrend: formatTrend(incomeCurrent, incomePrev),
      totalIncome: incomeTotal,
    };
  };

  const dashboardMetrics = computeDashboardMetrics();

  const formatRelativeTime = (dateValue) => {
    if (!dateValue) return "";
    const now = new Date();
    const date = new Date(dateValue);
    const diffMs = now - date;
    const minute = 60 * 1000;
    const hour = 60 * minute;
    const day = 24 * hour;
    const month = 30 * day;
    if (diffMs < minute) return "just now";
    if (diffMs < hour) return `${Math.floor(diffMs / minute)} min ago`;
    if (diffMs < day) return `${Math.floor(diffMs / hour)} hour${Math.floor(diffMs / hour) > 1 ? "s" : ""} ago`;
    if (diffMs < month) return `${Math.floor(diffMs / day)} day${Math.floor(diffMs / day) > 1 ? "s" : ""} ago`;
    return `${Math.floor(diffMs / month)} month${Math.floor(diffMs / month) > 1 ? "s" : ""} ago`;
  };

  const latestUser = [...users]
    .filter((u) => u?.createdAt)
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
  const latestTherapist = [...therapists]
    .filter((t) => t?.createdAt)
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];

  const userActivities = [
    latestUser && {
      label: `New user registered${latestUser.name ? `: ${latestUser.name}` : ""}`,
      time: latestUser.createdAt,
      color: "bg-green-500",
    },
  ].filter(Boolean);

  const therapistActivities = [
    latestTherapist && {
      label: `Therapist verified${latestTherapist.name ? `: ${latestTherapist.name}` : ""}`,
      time: latestTherapist.createdAt,
      color: "bg-blue-500",
    },
  ].filter(Boolean);

  // Modern icon mapping
  const getIconForCard = (key) => {
    switch (key) {
      case "totalTherapistCount":
        return <FiUserCheck className="w-8 h-8 text-blue-500" />;
      case "totalUserCount":
        return <FiUsers className="w-8 h-8 text-green-500" />;
      default:
        return <FiActivity className="w-8 h-8 text-purple-500" />;
    }
  };

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gray-50">
      <AdminSideBar />

      <div className="flex-1 p-4 md:p-6 lg:p-8">
        {/* Header Section */}
        <div className="mb-8">
          <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
            Dashboard Overview
          </h1>
          <p className="text-gray-600">
            Welcome back! Here's what's happening with your platform today.
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
          {OverviewData.length > 0 ? (
            OverviewData.map((card) => {
              let value = card.description;
              let trend = "0%";
              let trendColor = "text-green-600";

              let therapistStats = null;
              if (card.key === "totalTherapistCount") {
                value = therapists.length;
                trend = `${Math.max(0, therapists.length - 1)} new`; // simple delta
                const isApproved = t => t.approvalStatus === 'approved' || t.isApprovedByAdmin === true || t.isApprovedByAdmin === 'true';
                const isRejected = t => t.approvalStatus === 'rejected' || t.isApprovedByAdmin === false || t.isApprovedByAdmin === 'false';
                const isSuspended = t => t.isActive === false;
                therapistStats = {
                  active: therapists.filter(t => isApproved(t) && !isSuspended(t)).length,
                  pending: therapists.filter(t => !isApproved(t) && !isRejected(t) && !isSuspended(t)).length,
                  suspended: therapists.filter(t => isSuspended(t)).length,
                };
              }
              if (card.key === "totalUserCount") {
                value = users.length;
                trend = `${Math.max(0, users.length - 1)} new`;
              }
              if (card.key === "totalSessions") {
                value = dashboardMetrics.totalSessions;
                trend = dashboardMetrics.totalSessionsTrend.text;
                trendColor = dashboardMetrics.totalSessionsTrend.color;
              }
              if (card.key === "monthlyIncome") {
                value = dashboardMetrics.monthlyIncome;
                trend = dashboardMetrics.monthlyIncomeTrend.text;
                trendColor = dashboardMetrics.monthlyIncomeTrend.color;
              }

              return (
                <div
                  key={card.id}
                  className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 p-6 border border-gray-100 hover:border-gray-200 group"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="p-3 bg-gray-50 rounded-lg group-hover:bg-gray-100 transition-colors">
                      {getIconForCard(card.key)}
                    </div>
                    <div className="flex items-center space-x-1">
                      <FiTrendingUp className="w-4 h-4 text-green-500" />
                      <span className={`text-sm font-medium ${trendColor}`}>
                        {trend}
                      </span>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-gray-600 text-sm font-medium mb-2">
                      {card.title}
                    </h3>
                    <p className="text-3xl font-bold text-gray-900 mb-1">
                      {card.key === "monthlyIncome"
                        ? `£${Number(value || 0).toLocaleString()}`
                        : typeof value === "number"
                        ? value.toLocaleString()
                        : value}
                    </p>
                    {card.key === "monthlyIncome" && (
                      <p className="text-xs text-gray-500 mb-1">
                        Total: <span className="font-semibold text-gray-700">£{Number(dashboardMetrics.totalIncome || 0).toLocaleString()}</span>
                      </p>
                    )}
                    {therapistStats ? (
                      <div className="flex gap-2 mt-1 flex-wrap">
                        <span className="text-xs text-green-600 font-medium">
                          {therapistStats.active} active
                        </span>
                        <span className="text-xs text-yellow-600 font-medium">
                          {therapistStats.pending} pending
                        </span>
                        <span className="text-xs text-red-500 font-medium">
                          {therapistStats.suspended} suspended
                        </span>
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500">vs last month</p>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="col-span-full flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                <p className="text-gray-500">Loading dashboard data...</p>
              </div>
            </div>
          )}
        </div>

        {/* Additional Content Area */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Quick Actions Card */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Quick Actions
            </h3>
            <div className="space-y-3">
              <button
                className="w-full text-left p-3 rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-3"
                onClick={() => navigate("/adminSettings")}
              >
                <FiUsers className="w-5 h-5 text-blue-500" />
                <span className="text-gray-700">Manage Users</span>
              </button>
              <button
                className="w-full text-left p-3 rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-3"
                onClick={() => navigate("/adminTherapistInfo")}
              >
                <FiUserCheck className="w-5 h-5 text-green-500" />
                <span className="text-gray-700">Manage Therapists</span>
              </button>
              <button
                className="w-full text-left p-3 rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-3"
                onClick={() => navigate("/adminPayment")}
              >
                <FiActivity className="w-5 h-5 text-purple-500" />
                <span className="text-gray-700">View Analytics</span>
              </button>
            </div>
          </div>

          {/* Recent Activity Card */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Recent Activity
            </h3>
            <div className="space-y-4">
              <div>
                <div className="text-xs font-semibold text-gray-500 mb-2">Users</div>
                {userActivities.length === 0 ? (
                  <div className="text-xs text-gray-400">No recent user activity</div>
                ) : (
                  userActivities.map((activity, idx) => (
                    <div className="flex items-center space-x-3 mb-2 last:mb-0" key={`u-${idx}`}>
                      <div className={`w-2 h-2 ${activity.color} rounded-full`}></div>
                      <span className="text-sm text-gray-600">{activity.label}</span>
                      <span className="text-xs text-gray-400 ml-auto">
                        {formatRelativeTime(activity.time)}
                      </span>
                    </div>
                  ))
                )}
              </div>

              <div>
                <div className="text-xs font-semibold text-gray-500 mb-2">Therapists</div>
                {therapistActivities.length === 0 ? (
                  <div className="text-xs text-gray-400">No recent therapist activity</div>
                ) : (
                  therapistActivities.map((activity, idx) => (
                    <div className="flex items-center space-x-3 mb-2 last:mb-0" key={`t-${idx}`}>
                      <div className={`w-2 h-2 ${activity.color} rounded-full`}></div>
                      <span className="text-sm text-gray-600">{activity.label}</span>
                      <span className="text-xs text-gray-400 ml-auto">
                        {formatRelativeTime(activity.time)}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminHome;
